import { useState, useRef, useEffect } from 'react'
import { useNotifications } from '../contexts/NotificationContext'
import { useNavigate } from 'react-router-dom'
import './NotificationCenter.css'

const NotificationCenter = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('all')
  const dropdownRef = useRef(null)
  const navigate = useNavigate()
  
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAll,
    settings,
    updateSettings
  } = useNotifications()

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filtrar notificações por tipo
  const filteredNotifications = notifications.filter(notification => {
    if (activeTab === 'all') return true
    if (activeTab === 'unread') return !notification.read
    return notification.type === activeTab
  })

  // Obter ícone por prioridade
  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return '🔴'
      case 'medium': return '🟡'
      case 'low': return '🟢'
      default: return '⚪'
    }
  }

  // Formatar tempo relativo
  const getRelativeTime = (timestamp) => {
    const now = new Date()
    const diff = now - new Date(timestamp)
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Agora'
    if (minutes < 60) return `${minutes}min`
    if (hours < 24) return `${hours}h`
    return `${days}d`
  }

  // Lidar com clique na notificação
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id)
    }
    
    if (notification.data?.url) {
      navigate(notification.data.url)
      setIsOpen(false)
    }
  }

  // Contar notificações por tipo
  const getTypeCount = (type) => {
    return notifications.filter(n => n.type === type && !n.read).length
  }

  return (
    <div className="notification-center" ref={dropdownRef}>
      {/* Botão de notificações */}
      <button
        className={`notification-button ${unreadCount > 0 ? 'has-unread' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Notificações"
      >
        <span className="notification-icon">🔔</span>
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown de notificações */}
      {isOpen && (
        <div className="notification-dropdown">
          {/* Header */}
          <div className="notification-header">
            <h3>Notificações</h3>
            <div className="notification-actions">
              {unreadCount > 0 && (
                <button
                  className="mark-all-read"
                  onClick={markAllAsRead}
                  title="Marcar todas como lidas"
                >
                  ✓
                </button>
              )}
              <button
                className="clear-all"
                onClick={clearAll}
                title="Limpar todas"
              >
                🗑️
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="notification-tabs">
            <button
              className={`tab ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              Todas {notifications.length > 0 && `(${notifications.length})`}
            </button>
            <button
              className={`tab ${activeTab === 'unread' ? 'active' : ''}`}
              onClick={() => setActiveTab('unread')}
            >
              Não lidas {unreadCount > 0 && `(${unreadCount})`}
            </button>
            <button
              className={`tab ${activeTab === 'task' ? 'active' : ''}`}
              onClick={() => setActiveTab('task')}
            >
              Tarefas {getTypeCount('task') > 0 && `(${getTypeCount('task')})`}
            </button>
            <button
              className={`tab ${activeTab === 'event' ? 'active' : ''}`}
              onClick={() => setActiveTab('event')}
            >
              Eventos {getTypeCount('event') > 0 && `(${getTypeCount('event')})`}
            </button>
          </div>

          {/* Lista de notificações */}
          <div className="notification-list">
            {filteredNotifications.length === 0 ? (
              <div className="no-notifications">
                <span className="empty-icon">📭</span>
                <p>Nenhuma notificação</p>
              </div>
            ) : (
              filteredNotifications.map(notification => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.read ? 'unread' : ''} ${notification.priority}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-content">
                    <div className="notification-header-item">
                      <span className="notification-type-icon">
                        {notification.icon}
                      </span>
                      <span className="notification-title">
                        {notification.title}
                      </span>
                      <span className="notification-priority">
                        {getPriorityIcon(notification.priority)}
                      </span>
                      <span className="notification-time">
                        {getRelativeTime(notification.timestamp)}
                      </span>
                    </div>
                    <p className="notification-message">
                      {notification.message}
                    </p>
                  </div>
                  <div className="notification-actions-item">
                    {!notification.read && (
                      <button
                        className="mark-read"
                        onClick={(e) => {
                          e.stopPropagation()
                          markAsRead(notification.id)
                        }}
                        title="Marcar como lida"
                      >
                        ✓
                      </button>
                    )}
                    <button
                      className="remove"
                      onClick={(e) => {
                        e.stopPropagation()
                        removeNotification(notification.id)
                      }}
                      title="Remover"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer com configurações */}
          <div className="notification-footer">
            <button
              className="settings-button"
              onClick={() => {
                navigate('/settings')
                setIsOpen(false)
              }}
            >
              ⚙️ Configurações
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationCenter